import { navigateTo } from "./navigator"
import { getApiUrl } from "../config/urls"
import { socketService } from "../utils/socketService"
const API_BASE_URL = String(getApiUrl()).replace(/\/$/, "")

// Log pour déboguer l'URL de l'API

// Gestion centralisée des erreurs d'authentification (token expiré/invalide)
const handleUnauthorized = async (response, errorBody) => {
  const status = response?.status
  const message = (errorBody && (errorBody.message || errorBody.error)) || ""
  const isAuthError =
    status === 401 || status === 403 || /token/i.test(message) || /expired/i.test(message) || /jwt/i.test(message)
  // Cas particulier: fin d'essai/abonnement requis → l'appli gère via DemenageurLayout
  const isTrialOrSubscriptionCase =
    status === 403 && /(trial|essai|subscription|abonnement|contract|contrat)/i.test(message || "")

  if (isAuthError) {
    if (isTrialOrSubscriptionCase) {
      // Ne pas retirer les tokens ni rediriger: le layout montrera l'écran de blocage
      return
    }
    console.warn("Erreur d'authentification détectée:", { status, message })

    // Si le token a expiré, essayer de le rafraîchir
    if (status === 401 && message.includes("expired")) {
      try {
        const refreshToken = localStorage.getItem("refreshToken")
        if (refreshToken) {
          const refreshResponse = await fetch(`${API_BASE_URL}/auth/refresh-token`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ refreshToken }),
          })

          if (refreshResponse.ok) {
            const { token, refreshToken: newRefreshToken } = await refreshResponse.json()
            localStorage.setItem("token", token)
            if (newRefreshToken) {
              localStorage.setItem("refreshToken", newRefreshToken)
            }
            return // Ne pas rediriger si le rafraîchissement a réussi
          }
        }
      } catch (refreshError) {
        console.error("Erreur lors du rafraîchissement du token:", refreshError)
      }
    }

    // Si le rafraîchissement a échoué ou n'est pas possible, déconnecter l'utilisateur
    try {
      localStorage.removeItem("token")
      localStorage.removeItem("refreshToken")
      localStorage.removeItem("userType")
      localStorage.removeItem("user")

      // Rediriger vers la page de connexion avec l'URL de redirection
      if (typeof window !== "undefined" && window.location?.pathname !== "/login") {
        const currentPath = window.location.pathname + window.location.search
        window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`
      }
    } catch (error) {
      console.error("Erreur lors de la déconnexion:", error)
    }
  }
}

// Vérifier si le token est expiré
const isTokenExpired = (token) => {
  if (!token) return true
  try {
    const payload = JSON.parse(atob(token.split(".")[1]))
    return payload.exp * 1000 < Date.now()
  } catch (e) {
    console.error("Erreur lors de la vérification du token:", e)
    return true
  }
}

const fetchWithAuth = async (url, options = {}) => {
  // Récupérer le token depuis le localStorage
  let token = localStorage.getItem("token")
  const refreshToken = localStorage.getItem("refreshToken")

  // Vérifier si le token est expiré
  if (token && isTokenExpired(token)) {
    console.log("Token expiré, tentative de rafraîchissement...")
    try {
      if (refreshToken) {
        const refreshResponse = await fetch(`${API_BASE_URL}/auth/refresh-token`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken }),
        })

        if (refreshResponse.ok) {
          const { token: newToken, refreshToken: newRefreshToken } = await refreshResponse.json()
          if (newToken) {
            token = newToken
            localStorage.setItem("token", newToken)
            if (newRefreshToken) {
              localStorage.setItem("refreshToken", newRefreshToken)
            }
          }
        } else {
          console.warn("Échec du rafraîchissement du token")
          throw new Error("Échec du rafraîchissement du token")
        }
      } else {
        console.warn("Aucun refreshToken disponible pour rafraîchir le token")
        throw new Error("Session expirée, veuillez vous reconnecter")
      }
    } catch (refreshError) {
      console.error("Erreur lors du rafraîchissement du token:", refreshError)
      // Si le rafraîchissement échoue, supprimer les tokens et rediriger vers la page de connexion
      localStorage.removeItem("token")
      localStorage.removeItem("refreshToken")
      window.location.href = `/login?redirect=${encodeURIComponent(window.location.pathname)}`
      throw new Error("Session expirée, veuillez vous reconnecter")
    }
  }

  // Préparer les en-têtes
  const headers = {
    "Content-Type": "application/json",
    ...options.headers,
  }

  // Ajouter le token d'authentification s'il existe
  if (token) {
    headers["Authorization"] = `Bearer ${token}`
  } else {
    console.warn("No authentication token found in localStorage")
  }

  const response = await fetch(url, {
    ...options,
    headers,
  })

  if (!response.ok) {
    let errorMsg = "Une erreur est survenue"
    let errorBody = null
    try {
      const text = await response.text()
      errorBody = text ? JSON.parse(text) : {}
      errorMsg = errorBody.message || errorMsg
    } catch (_) {}
    await handleUnauthorized(response, errorBody)
    throw new Error(errorMsg)
  }

  // Check if response has content before trying to parse JSON
  const contentType = response.headers.get("content-type")
  if (contentType && contentType.includes("application/json")) {
    const data = await response.json()
    return data
  }

  // For 204 No Content or other empty responses
  return null
}

const navigateToFallback = (path, options = {}) => {
  if (typeof window !== "undefined" && window.location) {
    if (options.replace) {
      window.location.replace(path)
    } else {
      window.location.href = path
    }
  }
}

// Generic HTTP methods
const http = {
  get: (url, options = {}) => {
    const { params, ...rest } = options || {};
    let fullUrl = `${API_BASE_URL}${url}`;
    if (params && typeof params === "object") {
      const usp = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) {
        if (v === undefined || v === null) continue;
        usp.append(k, String(v));
      }
      const qs = usp.toString();
      if (qs) fullUrl += (fullUrl.includes("?") ? "&" : "?") + qs;
    }
    return fetchWithAuth(fullUrl, { ...rest, method: "GET" });
  },
  post: (url, data, options = {}) =>
    fetchWithAuth(`${API_BASE_URL}${url}`, {
      ...options,
      method: "POST",
      body: JSON.stringify(data),
    }),
  put: (url, data, options = {}) =>
    fetchWithAuth(`${API_BASE_URL}${url}`, {
      ...options,
      method: "PUT",
      body: JSON.stringify(data),
    }),
  delete: (url, options = {}) => fetchWithAuth(`${API_BASE_URL}${url}`, { ...options, method: "DELETE" }),
}

export const api = {
  ...http, // Spread the generic HTTP methods
  // Auth
  login: async (email, password, role) => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/auth/login`, {
        method: "POST",
        body: JSON.stringify({
          email,
          password,
          userType: role,
        }),
      })
      return response
    } catch (error) {
      // Si le serveur renvoie une erreur 403 avec un message personnalisé
      if (error.response && error.response.status === 403 && error.response.data && error.response.data.message) {
        throw new Error(error.response.data.message)
      }
      // Pour les autres erreurs, propager l'erreur d'origine
      throw error
    }
  },

  // Admin
  checkAdminExists: () => fetchWithAuth(`${API_BASE_URL}/admin/check-admin`),

  // Créer le premier administrateur (sans authentification)
  createFirstAdmin: (userData) =>
    fetch(`${API_BASE_URL}/admin/first-admin`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(userData),
    }).then(async (response) => {
      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}))
        await handleUnauthorized(response, errorBody)
        throw new Error(errorBody.message || "Erreur lors de la création de l'administrateur")
      }
      return response.json()
    }),

  // Créer un administrateur (nécessite authentification admin)
  createAdmin: (userData) =>
    fetchWithAuth(`${API_BASE_URL}/admin/create-admin`, {
      method: "POST",
      body: JSON.stringify(userData),
    }),

  // Admin Auth
  adminLogin: (email, password) =>
    fetchWithAuth(`${API_BASE_URL}/auth/login`, {
      method: "POST",
      body: JSON.stringify({
        email,
        password,
        forRole: "admin",
      }),
    }),

  register: (userData) =>
    fetchWithAuth(`${API_BASE_URL}/auth/register`, {
      method: "POST",
      body: JSON.stringify(userData),
    }),

  logout: async () => {
    const token = localStorage.getItem("token")
    try {
      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })
    } catch (error) {
      console.error("Erreur lors de la déconnexion:", error)
    }
  },

  // Client endpoints
  getClientQuotes: () => {
    return fetchWithAuth(`${API_BASE_URL}/moves/client/quotes`)
  },

  /**
   * Récupère les déménagements du client connecté
   * @returns {Promise<Array>} Liste des déménagements du client
   */
  getClientMoves: async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/moves/client`)

      // Vérifier si la réponse contient un tableau data et si success est true
      if (response && response.success && Array.isArray(response.data)) {
        return response.data
      }

      console.warn("Format de réponse inattendu pour les déménagements:", response)
      return []
    } catch (error) {
      console.error("Erreur lors de la récupération des déménagements:", error)
      return []
    }
  },

  /**
   * Récupère le profil de l'utilisateur connecté avec gestion améliorée des erreurs
   * @returns {Promise<Object>} Les données du profil utilisateur
   */
  getUserProfile: async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/auth/profile`)

      if (!response) {
        throw new Error("Aucune donnée reçue du serveur")
      }

      // Extraire les données de l'utilisateur de la réponse
      // Le serveur renvoie { success: true, data: {...} } ou { success: true, user: {...} } ou directement l'objet user
      let userData = response
      
      if (response && typeof response === "object") {
        // Si la réponse a une structure { success: true, data: {...} }
        if (response.success && response.data) {
          userData = response.data
        }
        // Si la réponse a une structure { success: true, user: {...} }
        else if (response.success && response.user) {
          userData = response.user
        }
        // Si la réponse contient directement un champ user
        else if (response.user) {
          userData = response.user
        }
        // Si la réponse est directement l'objet utilisateur (a un id)
        else if (response.id) {
          userData = response
        }
      }

      // Vérifier si les champs essentiels sont présents
      const requiredFields = ["id", "email", "role"]
      const missingFields = requiredFields.filter((field) => userData[field] === undefined || userData[field] === null)

      if (missingFields.length > 0) {
        console.warn("Champs manquants dans la réponse du profil:", missingFields)
      }

      // Normaliser les données de l'utilisateur
      const user = {
        id: userData.id,
        email: userData.email || "",
        role: userData.role || "client",
        firstName: userData.first_name || userData.firstName || "",
        lastName: userData.last_name || userData.lastName || "",
        phone: userData.phone || "",
        avatar: userData.avatar_url || userData.avatar || null,
        isVerified: userData.is_verified || userData.isVerified || false,
        // Utiliser la valeur de is_demenageur si elle existe, sinon déterminer en fonction du rôle
        is_demenageur: userData.is_demenageur !== undefined ? userData.is_demenageur : userData.role === "demenageur",
        address: userData.address || null,
        city: userData.city || null,
        postalCode: userData.postal_code || userData.postalCode || null,
        country: userData.country || null,
        createdAt: userData.created_at || userData.createdAt || null,
        updatedAt: userData.updated_at || userData.updatedAt || null,
        // Gérer les dates d'essai
        trial_start_date: userData.trial_start_date || userData.trialStartDate || null,
        trial_end_date: userData.trial_end_date || userData.trialEndDate || null,
        trial_used: userData.trial_used || userData.trialUsed || false,
        // Champs des documents
        cin_photo_url: userData.cin_photo_url || null,
        insurance_photo_url: userData.insurance_photo_url || null,
        registration_photo_url: userData.registration_photo_url || null,
        // Préserver les champs supplémentaires qui pourraient exister
        ...(userData.company_name && { companyName: userData.company_name }),
        ...(userData.siret && { siret: userData.siret }),
      }

      // Mettre en cache les données utilisateur dans le localStorage
      try {
        localStorage.setItem("user", JSON.stringify(user))
        localStorage.setItem("userRole", user.role)
      } catch (storageError) {
        console.error("Erreur lors de la mise en cache du profil utilisateur:", storageError)
        // Ne pas échouer à cause d'une erreur de stockage local
      }

      return user
    } catch (error) {
      console.error("Erreur lors de la récupération du profil utilisateur:", error)

      // En cas d'erreur 401, déconnecter l'utilisateur
      if (error.message.includes("401")) {
        try {
          localStorage.removeItem("token")
          localStorage.removeItem("user")
          localStorage.removeItem("userRole")
          if (typeof window !== "undefined" && window.location.pathname !== "/login") {
            window.location.href = "/login"
          }
        } catch (e) {
          console.error("Erreur lors de la déconnexion:", e)
        }
      }

      // Renvoyer un objet utilisateur minimal en cas d'erreur
      return {
        id: "unknown",
        email: "",
        role: "guest",
        firstName: "",
        lastName: "",
        isVerified: false,
      }
    }
  },

  /**
   * Récupère le profil client de l'utilisateur connecté (si applicable)
   */
  getClientProfile: async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/profiles/client`)
      // La réponse du serveur est { success: true, user: {...}, profile: {...} }
      // On retourne l'objet user fusionné avec le profile
      if (response && response.success) {
        return {
          ...response.user,
          ...(response.profile || {})
        }
      }
      return response
    } catch (error) {
      console.error("Erreur lors de la récupération du profil client:", error)
      // Return a default client profile if the endpoint is not found
      if (error.message && error.message.includes("404")) {
        return {
          id: "default-client",
          firstName: "Client",
          lastName: "Test",
          email: "client@example.com",
          phone: "",
          role: "client",
        }
      }
      throw error
    }
  },

  updateProfile: (payload) =>
    fetchWithAuth(`${API_BASE_URL}/auth/profile`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),

  /**
   * Récupère les statistiques de l'utilisateur connecté
   * @returns {Promise<Object>} Les statistiques de l'utilisateur
   */
  getUserStats: async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/auth/stats`)

      // Si la réponse est vide, retourner des valeurs par défaut
      if (!response) {
        console.warn("Aucune donnée de statistiques reçue du serveur")
        return {
          data: {
            totalMoves: 0,
            upcomingMoves: 0,
            totalSpent: 0,
            moveTypes: {},
          },
        }
      }

      // Retourner les données de la réponse
      return { data: response }
    } catch (error) {
      console.error("Erreur lors de la récupération des statistiques:", error)

      // En cas d'erreur, retourner des valeurs par défaut
      return {
        data: {
          totalMoves: 0,
          upcomingMoves: 0,
          totalSpent: 0,
          moveTypes: {},
        },
      }
    }
  },

  // Gestion du mot de passe
  changePassword: ({ currentPassword, newPassword, confirmPassword }) =>
    fetchWithAuth(`${API_BASE_URL}/auth/change-password`, {
      method: "POST",
      body: JSON.stringify({ currentPassword, newPassword, confirmPassword }),
    }),

  // Authentification à deux facteurs
  setup2FA: () =>
    fetchWithAuth(`${API_BASE_URL}/auth/2fa/setup`, {
      method: "POST",
    }),

  // Mot de passe oublié
  forgotPassword: async ({ email }) => {
    const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ email }),
    })

    const data = await response.json()

    if (!response.ok) {
      const error = new Error(data.message || "Une erreur est survenue")
      error.status = response.status
      error.data = data
      throw error
    }

    // Si le serveur renvoie un succès mais que l'utilisateur n'existe pas
    if (data.userNotFound) {
      const error = new Error("Aucun compte n'est associé à cette adresse email")
      error.userNotFound = true
      throw error
    }

    return data
  },

  // Réinitialisation du mot de passe
  resetPassword: async ({ token, newPassword, confirmPassword }) => {
    const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ token, newPassword, confirmPassword }),
    })

    const data = await response.json()

    if (!response.ok) {
      const error = new Error(data.message || "Une erreur est survenue lors de la réinitialisation du mot de passe")
      error.status = response.status
      error.data = data
      throw error
    }

    return data
  },

  verifyResetToken: async (token) => {
    const response = await fetch(
      `${API_BASE_URL}/auth/verify-reset-token?token=${encodeURIComponent(token)}`,
      { method: "GET", headers: { Accept: "application/json" } }
    )
    const data = await response.json()
    if (!response.ok) {
      const error = new Error(data.message || "Lien invalide ou expiré")
      error.status = response.status
      throw error
    }
    return data
  },

  // Vérification du jeton de réinitialisation
  verify2FACode(code) {
    return fetchWithAuth(`${API_BASE_URL}/auth/2fa/verify`, {
      method: "POST",
      body: JSON.stringify({ code }),
      headers: { "Content-Type": "application/json" },
    })
  },

  // Vérification d'email
  verifyEmail(token) {
    return fetchWithAuth(`${API_BASE_URL}/auth/verify-email?token=${token}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    })
  },

  // Vérification du téléphone pour déménageurs
  sendPhoneVerificationCode: async (phone) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/auth/phone/send-code`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ phone }),
    })
    return response
  },

  verifyPhoneCode: async (code) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/auth/phone/verify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code }),
    })
    return response
  },

  checkPhoneVerificationStatus: async () => {
    const response = await fetchWithAuth(`${API_BASE_URL}/auth/phone/status`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    })
    return response
  },

  disable2FA() {
    return fetchWithAuth(`${API_BASE_URL}/auth/2fa/disable`, {
      method: "POST",
    })
  },

  /**
   * Photo de profil : même flux que l’app mobile (stockage serveur, base64).
   * @param {File|Blob|FormData} fileOrFormData - fichier image, ou FormData avec clé `avatar` ou `file`
   */
  uploadAvatar: async (fileOrFormData) => {
    let file = fileOrFormData
    if (fileOrFormData instanceof FormData) {
      file = fileOrFormData.get("avatar") || fileOrFormData.get("file")
    }
    if (!file || typeof file === "string") {
      throw new Error("Fichier image requis")
    }
    const imageData = await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result)
      reader.onerror = () => reject(new Error("Lecture du fichier impossible"))
      reader.readAsDataURL(file)
    })
    const data = await fetchWithAuth(`${API_BASE_URL}/upload/profile-picture-base64`, {
      method: "POST",
      body: JSON.stringify({ imageData }),
    })
    const url = data?.imageUrl || data?.url
    return { ...data, avatarUrl: url }
  },

  getMoveById: (moveId) => fetchWithAuth(`${API_BASE_URL}/moves/client/moves/${moveId}`),
  moverStartMove: (moveId) => fetchWithAuth(`${API_BASE_URL}/moves/mover/moves/${moveId}/start`, { method: "POST" }),
  moverCompleteMove: (moveId) =>
    fetchWithAuth(`${API_BASE_URL}/moves/mover/moves/${moveId}/complete`, { method: "POST" }),

  getQuoteById: (quoteId) => fetchWithAuth(`${API_BASE_URL}/moves/client/quotes/${quoteId}`),

  // Quotes
  createQuote: (payload) =>
    fetchWithAuth(`${API_BASE_URL}/moves/devis`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  /** Demande de service adressée à un déménageur (même endpoint que l’app mobile) */
  createServiceRequest: (payload) =>
    fetchWithAuth(`${API_BASE_URL}/service-requests/create`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  /** Public — même route que l’app mobile (calendrier / créneaux occupés) */
  getMoverBusyDays: async (moverId) => {
    const res = await fetch(`${API_BASE_URL}/demenageurs/${encodeURIComponent(String(moverId))}/busy-days`)
    return res.json().catch(() => ({}))
  },
  updateQuote: (id, payload) =>
    fetchWithAuth(`${API_BASE_URL}/moves/client/quotes/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  deleteQuote: (id) =>
    fetchWithAuth(`${API_BASE_URL}/moves/client/quotes/${id}`, {
      method: "DELETE",
    }),

  // Quotes broadcast
  broadcastQuote: ({ lat, lng, radiusKm, truckType, description }) =>
    fetchWithAuth(`${API_BASE_URL}/moves/devis/broadcast`, {
      method: "POST",
      body: JSON.stringify({ lat, lng, radiusKm, truckType, description }),
    }),

  // AI
  analyzeMove: (text) =>
    fetchWithAuth(`${API_BASE_URL}/ai/analyze-move`, {
      method: "POST",
      body: JSON.stringify({ text }),
    }),

  analyzeImage: ({ image }) =>
    fetchWithAuth(`${API_BASE_URL}/ai/analyze-image`, {
      method: "POST",
      body: JSON.stringify({ image }),
    }),

  // Search
  // Recherche avancée de déménageurs avec pagination et filtres
  searchMovers: async ({
    lat,
    lng,
    radiusKm = 10,
    truckType,
    maxPrice,
    minRating = 0,
    query,
    page = 1,
    limit = 10,
    sortBy = "distance",
  }) => {
    const params = new URLSearchParams({
      lat,
      lng,
      radiusKm,
      page,
      limit,
      sortBy,
      minRating,
      ...(truckType && { truckType }),
      ...(maxPrice && { maxPrice }),
      ...(query && { query }),
    })

    return fetchWithAuth(`${API_BASE_URL}/search/movers?${params.toString()}`)
  },

  // Analyse de texte avec l'IA
  analyzeSearchQuery: (text) =>
    fetchWithAuth(`${API_BASE_URL}/ai/analyze`, {
      method: "POST",
      body: JSON.stringify({ text }),
    }),

  // Locations
  upsertMyLocation: ({ lat, lng, city, country }) =>
    fetchWithAuth(`${API_BASE_URL}/locations/me`, {
      method: "POST",
      body: JSON.stringify({ lat, lng, city, country }),
    }),

  // Mover quotes
  fetchMoverBroadcastQuotes: () => fetchWithAuth(`${API_BASE_URL}/moves/mover/quotes/broadcast`),
  fetchMyMoverQuotes: () => fetchWithAuth(`${API_BASE_URL}/moves/mover/quotes`),
  fetchMoverQuoteById: (id) => fetchWithAuth(`${API_BASE_URL}/moves/mover/quotes/${id}`),

  // Check if current user is assigned to a quote
  isAssignedToQuote: async (quoteId) => {
    try {
      const quote = await fetchWithAuth(`${API_BASE_URL}/moves/mover/quotes/${quoteId}`)
      return !!quote?.data?.mover_id
    } catch (error) {
      console.error("Error checking quote assignment:", error)
      return false
    }
  },
  updateMoverQuoteById: (id, payload) => {
    const requestPayload = { ...payload }

    // Ne pas forcer le statut s'il n'est pas fourni
    // Le backend gérera la logique de statut appropriée

    return fetchWithAuth(`${API_BASE_URL}/moves/mover/quotes/${id}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestPayload),
    })
  },
  getMoverMoveByQuoteId: (quoteId) => fetchWithAuth(`${API_BASE_URL}/moves/mover/moves/by-quote/${quoteId}`),

  // Disponibilités des déménageurs
  getMyAvailabilities: (month, year) => {
    const params = new URLSearchParams()
    if (month) params.append("month", month)
    if (year) params.append("year", year)
    return fetchWithAuth(`${API_BASE_URL}/availabilities/mover/me?${params.toString()}`)
  },

  saveMyAvailabilities: (availabilities) =>
    fetchWithAuth(`${API_BASE_URL}/availabilities/mover/me`, {
      method: "POST",
      body: JSON.stringify({ availabilities }),
    }),

  getMoverAvailabilities: (moverId, startDate, endDate) => {
    const params = new URLSearchParams()
    if (startDate) params.append("startDate", startDate)
    if (endDate) params.append("endDate", endDate)
    return fetchWithAuth(`${API_BASE_URL}/availabilities/mover/${moverId}?${params.toString()}`)
  },

  // Mover mission dates for calendar
  getMyMissionDates: (month, year) => {
    const params = new URLSearchParams()
    if (month) params.append("month", month)
    if (year) params.append("year", year)
    return fetchWithAuth(`${API_BASE_URL}/moves/mover/missions/dates?${params.toString()}`)
  },

  // Mover mission details by date
  getMyMissionByDate: (date) => {
    return fetchWithAuth(`${API_BASE_URL}/moves/mover/missions/date/${date}`)
  },

  // Mover toutes les missions (pour statistiques - inclut les terminées)
  getMoverAllMissions: (month, year) => {
    const params = new URLSearchParams()
    if (month) params.append("month", month)
    if (year) params.append("year", year)
    const url = `${API_BASE_URL}/moves/mover/missions/all${params.toString() ? '?' + params.toString() : ''}`
    console.log('📡 Appel API getMoverAllMissions:', url)
    return fetchWithAuth(url)
  },

  // Admin Dashboard
  getDashboardStats() {
    return fetchWithAuth(`${API_BASE_URL}/admin/dashboard`)
  },

  // Tickets
  async getClientTickets() {
    const response = await fetchWithAuth(`${API_BASE_URL}/tickets/me`)
    // Si la réponse est déjà un tableau, le retourner directement
    if (Array.isArray(response)) {
      return response
    }
    // Sinon, essayer d'extraire les données de la propriété data
    return response.data || []
  },

  async createTicket(ticketData) {
    try {
      const response = await fetch(`${API_BASE_URL}/tickets/me`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(ticketData),
      })

      const responseData = await response.json()

      if (!response.ok) {
        console.error("Error creating ticket:", responseData)
        throw new Error(responseData.message || "Failed to create ticket")
      }

      return responseData
    } catch (error) {
      console.error("Error in createTicket:", error)
      throw error
    }
  },

  updateTicket(id, ticketData) {
    return fetchWithAuth(`${API_BASE_URL}/tickets/me/${id}`, {
      method: "PUT",
      body: JSON.stringify(ticketData),
    })
  },

  async deleteTicket(id) {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/tickets/me/${id}`, {
        method: "DELETE",
      })

      // For 204 No Content, response will be null
      if (response === null) {
        return { success: true, message: "Ticket deleted successfully" }
      }

      // If we get here, there was an error
      console.error("Error deleting ticket:", response)
      throw new Error(response?.message || "Failed to delete ticket")
    } catch (error) {
      console.error("Error in deleteTicket:", error)
      throw error
    }
  },

  // Admin tickets
  getAllTickets({ status, type, page = 1, limit = 10 } = {}) {
    const params = new URLSearchParams()
    if (status) params.append("status", status)
    if (type) params.append("type", type)
    params.append("page", page)
    params.append("limit", limit)

    return fetchWithAuth(`${API_BASE_URL}/tickets/admin/all?${params.toString()}`)
  },

  assignTicket(ticketId, adminId) {
    return fetchWithAuth(`${API_BASE_URL}/tickets/admin/assign/${ticketId}`, {
      method: "POST",
      body: JSON.stringify({ adminId }),
    })
  },

  addInternalNote(ticketId, note) {
    return fetchWithAuth(`${API_BASE_URL}/tickets/admin/note/${ticketId}`, {
      method: "POST",
      body: JSON.stringify({ note }),
    })
  },

  // Télécharger un fichier
  downloadFile: async (url, filename) => {
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    })

    if (!response.ok) {
      let errorBody = null
      try {
        errorBody = await response.json()
      } catch (_) {}
      await handleUnauthorized(response, errorBody)
      throw new Error("Erreur lors du téléchargement du fichier")
    }

    const blob = await response.blob()
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = downloadUrl
    link.setAttribute("download", filename)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(downloadUrl)
  },

  // Téléverser un fichier
  uploadFile: async (file, onProgress) => {
    const formData = new FormData()
    formData.append("file", file)

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) {
          const percentComplete = Math.round((event.loaded / event.total) * 100)
          onProgress(percentComplete)
        }
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText)
            resolve(response)
          } catch (e) {
            reject(new Error("Invalid JSON response"))
          }
        } else {
          reject(new Error("Upload failed"))
        }
      }

      xhr.onerror = () => {
        reject(new Error("Erreur réseau lors du téléversement"))
      }

      xhr.open("POST", `${API_BASE_URL}/upload`, true)
      xhr.setRequestHeader("Authorization", `Bearer ${localStorage.getItem("token")}`)
      xhr.send(formData)
    })
  },

  // Contact support
  contactSupport({ quoteId, subject, message }) {
    return fetchWithAuth(`${API_BASE_URL}/support/contact`, {
      method: "POST",
      body: JSON.stringify({ quoteId, subject, message }),
    })
  },

  // Gestion de l'inventaire
  getClientInventory() {
    return fetchWithAuth(`${API_BASE_URL}/inventory`)
  },

  getInventoryItem(id) {
    return fetchWithAuth(`${API_BASE_URL}/inventory/${id}`)
  },

  createInventoryItem(itemData) {
    return fetchWithAuth(`${API_BASE_URL}/inventory`, {
      method: "POST",
      body: JSON.stringify(itemData),
    })
  },

  updateInventoryItem(id, itemData) {
    return fetchWithAuth(`${API_BASE_URL}/inventory/${id}`, {
      method: "PUT",
      body: JSON.stringify(itemData),
    })
  },

  deleteInventoryItem(id) {
    return fetchWithAuth(`${API_BASE_URL}/inventory/${id}`, {
      method: "DELETE",
    })
  },

  searchInventory(query) {
    return fetchWithAuth(`${API_BASE_URL}/inventory/search?q=${encodeURIComponent(query)}`)
  },

  getInventoryStats() {
    return fetchWithAuth(`${API_BASE_URL}/inventory/stats`)
  },

  // Gestion des abonnements
  getSubscription(userId) {
    return fetchWithAuth(`${API_BASE_URL}/subscriptions/${userId}/subscription`)
  },

  subscribeToPlan(planType, paymentMethod = "stripe") {
    return fetchWithAuth(`${API_BASE_URL}/subscription/subscribe`, {
      method: "POST",
      body: JSON.stringify({ planType, paymentMethod }),
    })
  },

  cancelSubscription() {
    return fetchWithAuth(`${API_BASE_URL}/subscription/cancel`, {
      method: "POST",
    })
  },

  updatePaymentMethod(paymentMethodId) {
    return fetchWithAuth(`${API_BASE_URL}/subscription/payment-method`, {
      method: "PUT",
      body: JSON.stringify({ paymentMethodId }),
    })
  },

  getInvoices() {
    return fetchWithAuth(`${API_BASE_URL}/subscription/invoices`)
  },

  getPaymentMethods() {
    return fetchWithAuth(`${API_BASE_URL}/subscription/payment-methods`)
  },

  addPaymentMethod(paymentMethod) {
    return fetchWithAuth(`${API_BASE_URL}/subscription/payment-methods`, {
      method: "POST",
      body: JSON.stringify({ paymentMethod }),
    })
  },

  removePaymentMethod(paymentMethodId) {
    return fetchWithAuth(`${API_BASE_URL}/subscription/payment-methods/${paymentMethodId}`, {
      method: "DELETE",
    })
  },

  // Gestion des offres de négociation
  getQuoteOffers(quoteId) {
    return fetchWithAuth(`${API_BASE_URL}/offers/quotes/${quoteId}/offers`)
  },

  async submitQuoteOffer(quoteId, { amount, message = "" }) {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/offers/quotes/${quoteId}/offers`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ amount, message, channel: 'web' }),
      })

      // Si la réponse est vide mais que la requête a réussi (statut 2xx)
      if (!response) {
        console.warn("Réponse vide du serveur lors de la création de l'offre")
        // Essayer de récupérer l'offre la plus récente
        const offers = await this.getQuoteOffers(quoteId)
        const latestOffer =
          Array.isArray(offers) && offers.length > 0
            ? offers[0]
            : { id: `temp-${Date.now()}`, amount, message, status: "pending", isOptimistic: true }
        return { data: latestOffer }
      }

      return response
    } catch (error) {
      console.error("Erreur lors de la soumission de l'offre:", error)
      // Si c'est une erreur 400 avec un message d'offre en attente, on la relance
      if (error.message.includes("déjà une offre en attente")) {
        throw error // On laisse le composant gérer ce cas spécifique
      }

      // Pour les autres erreurs, on retourne une offre optimiste
      return {
        data: {
          id: `temp-${Date.now()}`,
          quote_id: quoteId,
          amount,
          message,
          status: "pending",
          user_type: "mover",
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          isOptimistic: true,
          error: error.message,
        },
      }
    }
  },

  respondToOffer(quoteId, offerId, { status, message = "" }) {
    return fetchWithAuth(`${API_BASE_URL}/offers/quotes/${quoteId}/offers/${offerId}/respond`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ status, message }),
    })
  },

  getQuoteNegotiationHistory(quoteId) {
    return fetchWithAuth(`${API_BASE_URL}/offers/quotes/${quoteId}/history`)
  },

  // WebSocket négociation — socket partagé via socketService (évite une 3e connexion io par écran).
  connectToNegotiationUpdates(quoteId, onUpdate) {
    return socketService.subscribeNegotiation(quoteId, onUpdate)
  },
}

// Ensure navigateTo is defined
if (typeof navigateTo === "undefined") {
  window.navigateTo = navigateToFallback
}

// Ajout des méthodes manquantes pour la gestion des contrats
export const contractApi = {
  // Récupérer l'abonnement actif d'un utilisateur
  getActiveSubscription: async (userId) => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/contracts/user/${userId}/active-subscription`)
      return response
    } catch (error) {
      console.error("❌ Erreur dans getActiveSubscription:", {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
      })
      // Retourner une réponse vide au lieu de propager l'erreur
      return { data: null }
    }
  },

  // Récupérer le contrat de commission actif d'un utilisateur
  getActiveCommission: async (userId) => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/contracts/user/${userId}/active-commission`)
      return response
    } catch (error) {
      console.error("❌ Erreur dans getActiveCommission:", {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
      })
      // Retourner une réponse vide au lieu de propager l'erreur
      return { data: null }
    }
  },

  createContract: (contractData) =>
    fetchWithAuth(`${API_BASE_URL}/contracts`, {
      method: "POST",
      body: JSON.stringify(contractData),
    }),

  getTrialStatus: (userId) => fetchWithAuth(`${API_BASE_URL}/users/${userId}/trial-status`),

  // ─── Admin ──────────────────────────────────────────────────────────────────
  getDashboardStats: () => fetchWithAuth(`${API_BASE_URL}/admin/stats`),
  getAllUsers: ({ page = 1, limit = 100, role } = {}) => {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(Math.min(Math.max(limit, 1), 100)),
    })
    if (role) params.append("role", role)
    return fetchWithAuth(`${API_BASE_URL}/users?${params.toString()}`)
  },
  /** Valide un compte déménageur (admin) — active le compte et notifie en temps réel. */
  verifyDemenageurAccount: (userId) =>
    fetchWithAuth(`${API_BASE_URL}/users/${userId}/verify-demenageur`, { method: 'PUT' }),
  rejectDemenageurAccount: (userId, reason) =>
    fetchWithAuth(`${API_BASE_URL}/users/${userId}/reject-demenageur`, {
      method: 'PUT',
      body: JSON.stringify({ reason }),
    }),
  getMoverRegistrationDetails: (userId) =>
    fetchWithAuth(`${API_BASE_URL}/admin/users/${userId}/mover-registration`),
  getUserDetails: (userId) => fetchWithAuth(`${API_BASE_URL}/users/${userId}`),
  updateUser: (userId, userData) => fetchWithAuth(`${API_BASE_URL}/users/${userId}`, { method: 'PATCH', body: JSON.stringify(userData) }),
  deleteUser: (userId) => fetchWithAuth(`${API_BASE_URL}/users/${userId}`, { method: 'DELETE' }),
  deleteUsers: (userIds) => fetchWithAuth(`${API_BASE_URL}/users`, { method: 'DELETE', body: JSON.stringify({ ids: userIds }) }),
  createUser: async (userData) => {
    const token = localStorage.getItem("token")
    const url = `${API_BASE_URL}/admin/users`
    if (userData instanceof FormData) {
      const response = await fetch(url, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: userData,
      })
      if (!response.ok) {
        let errorMsg = "Une erreur est survenue"
        let errorBody = null
        try {
          const text = await response.text()
          errorBody = text ? JSON.parse(text) : {}
          errorMsg = errorBody.message || errorMsg
        } catch (_) {}
        await handleUnauthorized(response, errorBody)
        throw new Error(errorMsg)
      }
      const contentType = response.headers.get("content-type")
      if (contentType && contentType.includes("application/json")) {
        return response.json()
      }
      return { success: true }
    }
    return fetchWithAuth(url, { method: "POST", body: JSON.stringify(userData) })
  },
  banUser: (userId, reason) => fetchWithAuth(`${API_BASE_URL}/admin/users/${userId}/ban`, { method: 'POST', body: JSON.stringify({ reason }) }),
  unbanUser: (userId) => fetchWithAuth(`${API_BASE_URL}/admin/users/${userId}/unban`, { method: 'POST' }),
  addMoverMissionCredits: (userId, amount) =>
    fetchWithAuth(`${API_BASE_URL}/admin/users/${userId}/mission-credits`, {
      method: 'POST',
      body: JSON.stringify({ amount }),
    }),
  getAllReservations: () => fetchWithAuth(`${API_BASE_URL}/admin/reservations`),
  getMoveDetails: (moveId) => fetchWithAuth(`${API_BASE_URL}/admin/moves/${moveId}`),
  updateMoveStatus: (moveId, { status, reason } = {}) =>
    fetchWithAuth(`${API_BASE_URL}/admin/moves/${moveId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, reason }),
    }),
  getAllPayments: () => fetchWithAuth(`${API_BASE_URL}/admin/payments`),
  getPaymentDetails: (paymentId) => fetchWithAuth(`${API_BASE_URL}/admin/payments/${paymentId}`),
  getLogs: (params = {}) => {
    const q = new URLSearchParams();
    if (params.level) q.append('level', params.level);
    if (params.service) q.append('service', params.service);
    if (params.limit) q.append('limit', params.limit);
    if (params.offset) q.append('offset', params.offset);
    const qs = q.toString();
    return fetchWithAuth(`${API_BASE_URL}/admin/logs${qs ? `?${qs}` : ''}`);
  },
  getAdminConversations: () => fetchWithAuth(`${API_BASE_URL}/admin/conversations`),
  getAdminConversationMessages: (conversationId) => fetchWithAuth(`${API_BASE_URL}/admin/conversations/${conversationId}/messages`),
  getOrCreateAdminConversation: (userId) => fetchWithAuth(`${API_BASE_URL}/admin/conversations/user/${userId}`),
  sendAdminMessage: (userId, content) => fetchWithAuth(`${API_BASE_URL}/admin/messages`, { method: 'POST', body: JSON.stringify({ userId, content }) }),
  sendAdminVoiceMessage: (formData) => {
    const token = localStorage.getItem('token');
    return fetch(`${API_BASE_URL}/admin/messages/voice`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    }).then(async (res) => {
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.message || 'Erreur'); }
      return res.json();
    });
  },
  getAllTickets: (params = {}) => {
    const q = new URLSearchParams();
    if (params.status) q.append('status', params.status);
    if (params.type) q.append('type', params.type);
    if (params.search) q.append('search', params.search);
    const qs = q.toString();
    return fetchWithAuth(`${API_BASE_URL}/admin/tickets${qs ? `?${qs}` : ''}`);
  },
  getTicketDetails: (ticketId) => fetchWithAuth(`${API_BASE_URL}/admin/tickets/${ticketId}`),
  updateTicketStatus: (ticketId, status, assignedTo) => fetchWithAuth(`${API_BASE_URL}/admin/tickets/${ticketId}`, { method: 'PATCH', body: JSON.stringify({ status, assigned_to: assignedTo }) }),
  getUsersWithConnectionStatus: () => fetchWithAuth(`${API_BASE_URL}/admin/users/connection-status`),
  getCompleteAppStats: () => fetchWithAuth(`${API_BASE_URL}/admin/stats/complete`),
  getUsageByZone: (period = '30d') =>
    fetchWithAuth(`${API_BASE_URL}/admin/stats/usage-by-zone?period=${encodeURIComponent(period)}`),
  getAdminNotificationsFeed: (params = {}) => {
    const q = new URLSearchParams();
    if (params.limit) q.append('limit', params.limit);
    if (params.offset) q.append('offset', params.offset);
    const qs = q.toString();
    return fetchWithAuth(`${API_BASE_URL}/admin/notifications-feed${qs ? `?${qs}` : ''}`);
  },
  deleteAdminNotification: (id) =>
    fetchWithAuth(`${API_BASE_URL}/admin/notifications-feed/${id}`, { method: 'DELETE' }),
  searchNotificationRecipients: (role, search = '') => {
    const q = new URLSearchParams({ role });
    if (search) q.append('search', search);
    return fetchWithAuth(`${API_BASE_URL}/admin/notifications/recipients?${q.toString()}`);
  },
  broadcastAdminNotification: (body) =>
    fetchWithAuth(`${API_BASE_URL}/admin/notifications/broadcast`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  detectFrauds: () => fetchWithAuth(`${API_BASE_URL}/admin/frauds/detect`),
  getContentReports: () => fetchWithAuth(`${API_BASE_URL}/admin/moderation/reports`),
  resolveReport: (reportId, action, notes) => fetchWithAuth(`${API_BASE_URL}/admin/moderation/reports/${reportId}/resolve`, { method: 'POST', body: JSON.stringify({ action, notes }) }),
  getAllSubscriptionPlans: () => fetchWithAuth(`${API_BASE_URL}/admin/subscriptions/plans`),
  getAllUserSubscriptions: () => fetchWithAuth(`${API_BASE_URL}/admin/subscriptions/all`),
  getSubscriptionStats: () => fetchWithAuth(`${API_BASE_URL}/admin/subscriptions/stats`),
  createSubscriptionPlan: (planData) => fetchWithAuth(`${API_BASE_URL}/admin/subscriptions/plans`, { method: 'POST', body: JSON.stringify(planData) }),
  updateSubscriptionPlan: (planId, planData) => fetchWithAuth(`${API_BASE_URL}/admin/subscriptions/plans/${planId}`, { method: 'PUT', body: JSON.stringify(planData) }),
  getSystemSettings: () => fetchWithAuth(`${API_BASE_URL}/admin/settings/system`),
  updateSystemSettings: (config) =>
    fetchWithAuth(`${API_BASE_URL}/admin/settings/system`, {
      method: 'PUT',
      body: JSON.stringify(config),
    }),
  getPublicSystemSettings: () => fetch(`${API_BASE_URL}/settings/public`).then((r) => r.json()),
  getUserSubscriptionDetails: (subscriptionId) => fetchWithAuth(`${API_BASE_URL}/admin/subscriptions/${subscriptionId}`),
  getSystemMetrics: () => fetchWithAuth(`${API_BASE_URL}/admin/system-monitoring/metrics`),
  getAllNegotiations: () => fetchWithAuth(`${API_BASE_URL}/admin/negotiations`),
  getAllPromotions: () => fetchWithAuth(`${API_BASE_URL}/admin/promotions`),
  getPromotion: (id) => fetchWithAuth(`${API_BASE_URL}/admin/promotions/${id}`),
  createPromotion: (data) => fetchWithAuth(`${API_BASE_URL}/admin/promotions`, { method: 'POST', body: JSON.stringify(data) }),
  updatePromotion: (id, data) => fetchWithAuth(`${API_BASE_URL}/admin/promotions/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deletePromotion: (id) => fetchWithAuth(`${API_BASE_URL}/admin/promotions/${id}`, { method: 'DELETE' }),
  togglePromotionStatus: (id) => fetchWithAuth(`${API_BASE_URL}/admin/promotions/${id}/toggle-status`, { method: 'POST' }),
  getPromotionsStats: () => fetchWithAuth(`${API_BASE_URL}/admin/promotions/stats`),
  getReferralSettings: () => fetchWithAuth(`${API_BASE_URL}/admin/referrals/settings`),
  updateReferralSettings: (data) =>
    fetchWithAuth(`${API_BASE_URL}/admin/referrals/settings`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  getReferralStats: () => fetchWithAuth(`${API_BASE_URL}/admin/referrals/stats`),
  getReferralLeaderboard: () => fetchWithAuth(`${API_BASE_URL}/admin/referrals/leaderboard`),
  getAllReviews: () => fetchWithAuth(`${API_BASE_URL}/admin/reviews`),
  deleteReview: (id) => fetchWithAuth(`${API_BASE_URL}/admin/reviews/${id}`, { method: 'DELETE' }),
  getAllMarketingCampaigns: () => fetchWithAuth(`${API_BASE_URL}/admin/marketing/campaigns`),
  getAllCategories: () => fetchWithAuth(`${API_BASE_URL}/admin/categories`),
  getAllEmailTemplates: () => fetchWithAuth(`${API_BASE_URL}/admin/email-templates`),
  getEmailTemplate: (id) => fetchWithAuth(`${API_BASE_URL}/admin/email-templates/${id}`),
  createEmailTemplate: (data) => fetchWithAuth(`${API_BASE_URL}/admin/email-templates`, { method: 'POST', body: JSON.stringify(data) }),
  updateEmailTemplate: (id, data) => fetchWithAuth(`${API_BASE_URL}/admin/email-templates/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteEmailTemplate: (id) => fetchWithAuth(`${API_BASE_URL}/admin/email-templates/${id}`, { method: 'DELETE' }),
  duplicateEmailTemplate: (id) => fetchWithAuth(`${API_BASE_URL}/admin/email-templates/${id}/duplicate`, { method: 'POST' }),
  sendTestEmail: (id, testEmail, variables) => fetchWithAuth(`${API_BASE_URL}/admin/email-templates/${id}/test`, { method: 'POST', body: JSON.stringify({ testEmail, variables }) }),
  toggleEmailTemplateStatus: (id) => fetchWithAuth(`${API_BASE_URL}/admin/email-templates/${id}/toggle-status`, { method: 'POST' }),
  checkSmtpConfig: () => fetchWithAuth(`${API_BASE_URL}/admin/email-templates/smtp/check`),
  getEmailTemplateLogs: (id, limit = 100, offset = 0) => fetchWithAuth(`${API_BASE_URL}/admin/email-templates/${id}/logs?limit=${limit}&offset=${offset}`),
  getEmailTemplateHistory: (id) => fetchWithAuth(`${API_BASE_URL}/admin/email-templates/${id}/history`),
  getEmailTemplateStats: (id) => fetchWithAuth(`${API_BASE_URL}/admin/email-templates/${id}/stats`),
  getAllEmailTemplatesStats: () => fetchWithAuth(`${API_BASE_URL}/admin/email-templates/stats`),
  generateReport: async (reportType, startDate, endDate) => {
    const res = await fetchWithAuth(`${API_BASE_URL}/admin/reports/generate`, {
      method: 'POST',
      body: JSON.stringify({ reportType, startDate, endDate })
    });
    return res && res.json ? res.json() : res;
  },
}

// Exporter les méthodes de l'API de contrat via l'objet api principal
Object.assign(api, contractApi)
export { fetchWithAuth, API_BASE_URL }
